import * as React from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { getConnectionSummary, listAvailabilityPatterns } from "@/api/client";

import { flashElementById } from "@/lib/flash";

import { BackBar } from "@/components/BackBar";
import { PageTitle } from "@/components/PageTitle";
import { BuilderReturnCrumb } from "../booking/BuilderReturnCrumb";
import { CalendarConnectionsPanel } from "./CalendarConnectionsPanel";
import { PersonalTokensPanel } from "./PersonalTokensPanel";
import { PlanUsagePanel } from "./PlanUsagePanel";
import { TokenUsageHeatmapPanel } from "./TokenUsageHeatmapPanel";
import { HandleMigrationPanel } from "./HandleMigrationPanel";
import { ProfileForm } from "./ProfileForm";
import { ProfileProgress, ProgressStep } from "./ProfileProgress";
import { useProfile } from "./ProfileContext";
import { IntegrationsProvider, useIntegrations } from "./IntegrationsContext";
import { useFeature } from "./useFeature";
import { activeProviders, availableProviders } from "@/lib/calendarProviders";

/**
 * Profile settings, reachable from the main view (unlike {@link
 * ProfileSetupPage}, which is the one-time handle gate). Two-column: a left panel
 * (like the booking page's "Your live bookings") holds the goal-gradient setup
 * tracker with the connected calendar listed beneath it; the editable form is on the
 * right. The form itself lives in {@link ProfileForm} — shared read-only with
 * AdminUserProfilePage.
 */
export function ProfilePage() {
  // The provider has to sit outside everything that reads it, including the setup checklist below,
  // so the page is a thin shell around one owner of the calendar-connection state.
  return (
    <IntegrationsProvider>
      <ProfilePageContent />
    </IntegrationsProvider>
  );
}

function ProfilePageContent() {
  const { state, setProfile } = useProfile();
  const [searchParams] = useSearchParams();
  const justOnboarded = searchParams.get("welcome") === "1";

  // Deep links like /profile#calendar-integrations (the booking builder's "Connect Google" nudge)
  // and /profile#availability-pattern-<id> (a rolling link pointing at the pattern it owns) scroll
  // to that element and flash it. Every one of these targets renders after a fetch, so poll for it
  // rather than looking once: on a cold API call the section can be seconds behind the route.
  const location = useLocation();
  React.useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    // Polling is this caller's own problem — the flash itself is shared with the delivery switch's
    // "Connect a calendar", which points at a panel that is already mounted.
    let tries = 0;
    let timer = 0;
    let cancelFlash: (() => void) | null = null;
    const tick = () => {
      cancelFlash = flashElementById(id);
      if (!cancelFlash && tries++ < 40) timer = window.setTimeout(tick, 100);
    };
    timer = window.setTimeout(tick, 100);
    return () => {
      window.clearTimeout(timer);
      cancelFlash?.();
    };
  }, [location.hash]);

  // Drive the goal-gradient tracker from the real setup state: calendar, meeting
  // types, and connections. calAvailable also decides whether the calendar sidebar
  // (and the calendar step) shows at all.
  // The async half. Kept as raw counts rather than finished steps so the checklist can also read
  // the profile, which changes on save without needing any of this refetched.
  // The calendar half comes from the page's shared state, so disconnecting in the panel unticks
  // this step at the same moment it locks the delivery choice.
  const { iv, loaded: calLoaded } = useIntegrations();
  const aiScheduling = useFeature("AI_SCHEDULING");
  const [counts, setCounts] = React.useState<{
    typeCount: number;
    friends: number;
  } | null>(null);
  React.useEffect(() => {
    Promise.allSettled([listAvailabilityPatterns(), getConnectionSummary()]).then(([mt, conn]) => {
      setCounts({
        typeCount: mt.status === "fulfilled" ? mt.value.length : 0,
        friends: conn.status === "fulfilled" ? conn.value.accepted : 0,
      });
    });
  }, [searchParams]);

  const socialCount =
    state.status === "ready" ? (state.profile.socialLinks?.length ?? 0) : 0;
  const setup = React.useMemo(() => {
    if (!counts || !calLoaded) return null;
    // activeProviders, not connections: a connection the provider has rejected is one the host
    // still has to act on, so ticking the step off would be telling them the opposite.
    const calConnected = activeProviders(iv).length > 0;
    const steps: ProgressStep[] = [
      { label: "Signed up", done: true },
      { label: "Add your availability patterns", done: counts.typeCount > 1 },
      ...(availableProviders(iv).length > 0
        ? [{ label: "Connect a calendar", done: calConnected }]
        : []),
      // Optional, and a checklist step rather than a setup-form field on purpose: the host is
      // already here right after signing up, the fields are just below, and a step asks without
      // ever blocking them.
      { label: "Add your links", done: socialCount > 0 },
      { label: "Connect with friends", done: counts.friends > 0 },
    ];
    return { steps };
  }, [counts, calLoaded, iv, socialCount]);

  const profile = state.status === "ready" ? state.profile : null;
  if (!profile) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-8 font-comic-body text-sm text-muted-foreground">
        Loading your profile…
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl space-y-6 px-4 py-8 font-comic-body">
      {/* Two ways back, and never both: the crumb is for an errand that left the app (a calendar's
          OAuth round trip, which router state cannot survive), the bar for an in-app deep link —
          a rolling link sending the host to the availability pattern it owns. */}
      <BuilderReturnCrumb />
      <BackBar />
      <header>
        <PageTitle>Your profile</PageTitle>
      </header>

      {justOnboarded && (
        <div className="rounded-xl bg-brand-blue/10 px-4 py-3 text-sm text-foreground">
          You're all set, @{profile.handle}! Connect a calendar so your agent works around
          your real schedule.
        </div>
      )}

      {profile.handleNeedsMigration && profile.handle && (
        <HandleMigrationPanel currentHandle={profile.handle} onRenamed={setProfile} />
      )}

      <div className="flex flex-col gap-6 md:flex-row md:items-start">
        {setup && (
          <aside className="space-y-3 md:sticky md:top-20 md:w-60 md:shrink-0">
            <ProfileProgress steps={setup.steps} />
            <PlanUsagePanel />
            <CalendarConnectionsPanel />
            <PersonalTokensPanel />
          </aside>
        )}
        <div className="min-w-0 flex-1">
          <ProfileForm profile={profile} onSaved={setProfile} />
        </div>
      </div>

      {/* Tokens are spent by agent-to-agent scheduling, so without that feature the panel is a
          heatmap of zeroes explaining a cost the host cannot incur. Hidden rather than upsold,
          like the other AI_SCHEDULING surfaces. */}
      {aiScheduling === "granted" && (
        <div className="mx-auto w-full max-w-xl">
          <TokenUsageHeatmapPanel />
        </div>
      )}
    </main>
  );
}
